import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useNavigate } from "react-router-dom";
import { ShoppingBasket, Check, Minus, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { formatRupiah } from "../lib/format";
import { stockBadge } from "../lib/storage";
import { getProductEduNote } from "../lib/productEdu";
import { useApp } from "../context/AppContext";

const toneClass = {
  success: "bg-emerald-100 text-emerald-700 border-emerald-200",
  warning: "bg-amber-100 text-amber-700 border-amber-200",
  danger: "bg-rose-100 text-rose-700 border-rose-200",
};

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { cart, addToCart, setCartQty } = useApp();
  const badge = stockBadge(product.stock);
  const soldOut = product.stock <= 0;
  const inCart = cart.find((i) => i.productId === product.id);
  const qty = inCart?.quantity || 0;
  const eduNote = getProductEduNote(product);
  const categoryName =
    typeof product.category === "string" ? product.category : product.category?.name || "Produk Herbal";

  const detailPath = `/produk/${product.slug}`;

  const goToDetail = () => {
    navigate(detailPath);
  };

  const handleCardKeyDown = (event) => {
    if (event.target.closest("button,a")) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      goToDetail();
    }
  };

  const handleAdd = (event) => {
    event?.stopPropagation();
    if (qty >= product.stock) {
      toast.warning("Jumlah sudah mencapai batas stok");
      return;
    }
    addToCart(product.id, 1);
    toast.success(`${product.name} ditambahkan ke keranjang`);
  };

  const inc = (event) => {
    event?.stopPropagation();
    if (qty >= product.stock) {
      toast.warning("Stok tidak mencukupi");
      return;
    }
    setCartQty(product.id, qty + 1);
  };
  const dec = (event) => {
    event?.stopPropagation();
    setCartQty(product.id, qty - 1);
  };

  return (
    <article
      className={`group bg-white rounded-2xl border ${
        inCart ? "border-emerald-500 ring-2 ring-emerald-200" : "border-stone-200"
      } shadow-sm hover-lift flex flex-col overflow-hidden cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2`}
      data-testid={`product-card-${product.id}`}
      role="link"
      tabIndex={0}
      aria-label={`Lihat detail produk ${product.name}`}
      onClick={goToDetail}
      onKeyDown={handleCardKeyDown}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-stone-100">
        <img
          src={product.image}
          alt={`${product.name} dari Teras Alami, ${categoryName.toLowerCase()} premium`}
          className="w-full h-full object-cover transition duration-500 group-hover:scale-105"
          loading="lazy"
          decoding="async"
        />
        <span
          className={`absolute top-3 left-3 text-xs font-medium px-3 py-1 rounded-full border ${toneClass[badge.tone]}`}
        >
          {badge.label}
        </span>
        {inCart && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-emerald-600 text-white text-xs font-medium px-3 py-1 rounded-full">
            <Check className="w-3.5 h-3.5" /> Di keranjang
          </span>
        )}
      </div>

      <div className="p-5 sm:p-6 flex flex-col flex-1">
        <Badge
          variant="outline"
          className="w-fit bg-stone-50 text-stone-600 border-stone-200 mb-3"
        >
          {categoryName}
        </Badge>
        <h3 className="font-display text-xl font-semibold text-stone-900 mb-1 group-hover:text-emerald-700">
          {product.name}
        </h3>
        <p className="text-stone-600 text-sm leading-relaxed mb-3 line-clamp-2">
          {product.benefit}
        </p>

        {eduNote && (
          <div
            className="mb-4 flex gap-2 items-start bg-purple-50/70 border border-purple-100 rounded-xl px-3 py-2"
            data-testid={`edu-${product.id}`}
          >
            <Sparkles className="w-3.5 h-3.5 text-purple-600 mt-0.5 shrink-0" />
            <p className="text-xs text-purple-800 leading-snug">{eduNote}</p>
          </div>
        )}

        <div className="mt-auto flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-stone-500">Harga</p>
            <p className="font-display text-xl font-semibold text-emerald-700">
              {formatRupiah(product.price)}
            </p>
            {product.compare_price && Number(product.compare_price) > Number(product.price) && (
              <p className="text-sm text-stone-400 line-through">
                {formatRupiah(product.compare_price)}
              </p>
            )}
          </div>

          {inCart ? (
            <div
              className="flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded-xl p-1"
              onClick={(event) => event.stopPropagation()}
            >
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={dec}
                className="h-10 w-10 text-emerald-700 hover:bg-emerald-100"
                data-testid={`cart-dec-${product.id}`}
              >
                <Minus className="w-4 h-4" />
              </Button>
              <span
                className="w-8 text-center font-semibold text-emerald-800"
                data-testid={`cart-qty-${product.id}`}
              >
                {qty}
              </span>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={inc}
                className="h-10 w-10 text-emerald-700 hover:bg-emerald-100"
                data-testid={`cart-inc-${product.id}`}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <Button
                type="button"
                onClick={handleAdd}
                disabled={soldOut}
                className="h-12 px-5 rounded-xl text-base bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-stone-200 disabled:text-stone-400"
                data-testid={`add-to-cart-${product.id}`}
              >
                <ShoppingBasket className="w-5 h-5 mr-2" />
                {soldOut ? "Habis" : "Tambah"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default ProductCard;
